import { Button, Col, Result, Row, Typography } from 'antd'
import { ITransaction, useCombineTransfer, useTransactions } from 'api'
import { TransactionType } from 'api/transactions/dtos'
import { useHideModal } from 'components/Modals'
import { useLocalization } from 'localization'
import { FC, useState } from 'react'
import Styled from './styled'
import Skeleton from './skeleton'
import Container from 'components/Container'
import TransactionsTable from './TransactionsTable'
import { useTheme } from 'styled-components'
import TransactionDetails from '../TransactionDetails'

const CreateTransferModal: FC = () => {
	const { t } = useLocalization()
	const theme = useTheme()
	const hideModal = useHideModal()
	const [selectedOutflow, selectOutflow] = useState<ITransaction>()
	const [selectedInflow, selectInflow] = useState<ITransaction>()
	const { transactions: outflowTransactions = [], isFetched: isOutflowFetched } = useTransactions({
		page: 0,
		size: 9999,
		types: [
			TransactionType.CRYPTO_DEBT_REPAYMENT,
			TransactionType.CRYPTO_EXPENSE,
			TransactionType.CRYPTO_OTHER_FEES,
			TransactionType.CRYPTO_LOST_OR_STOLEN,
			TransactionType.CRYPTO_SEND_GIFT_OR_DONATION,
			TransactionType.CRYPTO_SEND_PAYMENT,
			TransactionType.CRYPTO_SEND_TO_UNKNOWN,
			TransactionType.CRYPTO_SEND_NON_TAXABLE,
			TransactionType.CRYPTO_LIQUIDATION,
		],
	})
	const { transactions: inflowTransactions = [], isFetched: isInflowFetched } = useTransactions({
		page: 0,
		size: 9999,
		types: [
			TransactionType.CRYPTO_INCOME_REWARD,
			TransactionType.CRYPTO_INTEREST_INCOME,
			TransactionType.CRYPTO_STAKING_REWARD,
			TransactionType.CRYPTO_AIRDROP,
			TransactionType.CRYPTO_FORK,
			TransactionType.CRYPTO_MINING,
			TransactionType.CRYPTO_RECEIVE_NON_TAXABLE,
			TransactionType.CRYPTO_RECEIVE_PAYMENT,
			TransactionType.CRYPTO_RECEIVE_GIFT_OR_DONATION,
			TransactionType.CRYPTO_BORROWING,
			TransactionType.CRYPTO_ICO_IDO,
			TransactionType.CRYPTO_SPAM,
		],
	})
	const { mutateAsync: combineIntoTransfer, isLoading } = useCombineTransfer()

	const onSubmit = async () => {
		if (selectedInflow && selectedOutflow) {
			await combineIntoTransfer({
				inflowId: selectedInflow.id,
				outflowId: selectedOutflow.id,
			})
			hideModal()
		}
	}

	const isFetched = isOutflowFetched && isInflowFetched

	return (
		<Styled.Modal
			title={t('transactions.createTransferModal.title')}
			width={1200}
			open
			onCancel={hideModal}
			footer={null}
		>
			{!isFetched && <Skeleton />}
			{isFetched && (outflowTransactions.length === 0 || inflowTransactions.length === 0) && (
				<Result
					title={t('transactions.createTransferModal.emptyState.title')}
					subTitle={t('transactions.createTransferModal.emptyState.subtitle')}
				/>
			)}
			{isFetched && outflowTransactions.length > 0 && inflowTransactions.length > 0 && (
				<Container space="md" spaceDirection="vertical">
					<Typography.Paragraph italic>{t('transactions.createTransferModal.description')}</Typography.Paragraph>
					<Row gutter={[theme.margins.sm, theme.margins.sm]}>
						<Col xs={24} lg={12}>
							<Typography.Paragraph strong>{t('terms.outgoing')}</Typography.Paragraph>
							{selectedOutflow ? (
								<Container space="md" spaceDirection="vertical">
									<Typography.Link
										onClick={e => {
											e.preventDefault()
											selectOutflow(undefined)
										}}
									>
										{t('actions.back')}
									</Typography.Link>
									<TransactionDetails {...selectedOutflow} short />
								</Container>
							) : (
								<TransactionsTable
									transactions={outflowTransactions.filter(
										({ out }) => !selectedInflow || selectedInflow.in?.currency === out?.currency
									)}
									onSelect={selectOutflow}
								/>
							)}
						</Col>
						<Col xs={24} lg={12}>
							<Typography.Paragraph strong>{t('terms.incoming')}</Typography.Paragraph>
							{selectedInflow ? (
								<Container space="md" spaceDirection="vertical">
									<Typography.Link
										onClick={e => {
											e.preventDefault()
											selectInflow(undefined)
										}}
									>
										{t('actions.back')}
									</Typography.Link>
									<TransactionDetails {...selectedInflow} short />
								</Container>
							) : (
								<TransactionsTable
									transactions={inflowTransactions.filter(
										({ in: _in }) => !selectedOutflow || selectedOutflow.out?.currency === _in?.currency
									)}
									onSelect={selectInflow}
								/>
							)}
						</Col>
					</Row>
					<Button type="primary" loading={isLoading} disabled={!selectedInflow || !selectedOutflow} onClick={onSubmit}>
						{t('actions.combine')}
					</Button>
				</Container>
			)}
		</Styled.Modal>
	)
}

export default CreateTransferModal
